import { useMemo } from "react";
import { Link } from "react-router-dom";
import { dataset, supportedCountryCodes } from "@/data";
import canonical from "@data/canonical-agents.json";
import type { Cell, Confidence } from "@/types";
import { BarRow } from "@/components/charts";
import { pct } from "@/format";

const slug = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, "");

// Agent scalar fields we track coverage for (label -> accessor).
const FIELDS: { label: string; key: keyof typeof dataset.agents[number] }[] = [
  { label: "Website", key: "website" },
  { label: "Status", key: "status" },
  { label: "HQ country", key: "country" },
  { label: "Founding year", key: "foundingYear" },
  { label: "Languages", key: "languages" },
  { label: "Currencies", key: "currencies" },
  { label: "Payment methods", key: "paymentMethods" },
  { label: "Support channels", key: "supportChannels" },
  { label: "Service fee", key: "serviceFee" },
  { label: "Insurance offered", key: "insuranceOffered" },
  { label: "Free storage days", key: "freeStorageDays" },
  { label: "Consolidation", key: "consolidation" },
  { label: "Repackaging", key: "repackaging" },
  { label: "Free QC photos", key: "freePhotos" },
  { label: "Trustpilot", key: "trustpilot" },
  { label: "Reddit sentiment", key: "redditSentiment" },
];

function isCell(x: unknown): x is Cell<unknown> {
  return !!x && typeof x === "object" && "v" in (x as object) && "conf" in (x as object);
}

export default function CoveragePage() {
  const stats = useMemo(() => {
    const agents = dataset.agents;
    const n = agents.length;

    const fieldCoverage = FIELDS.map((f) => {
      let known = 0;
      for (const a of agents) {
        const c = a[f.key] as unknown;
        if (isCell(c) && c.v !== null) known++;
      }
      return { label: f.label, known, pct: known / n };
    });

    // Confidence distribution across all tracked scalar cells + feature cells.
    const conf: Record<Confidence, number> = { high: 0, medium: 0, low: 0, unknown: 0 };
    let cellTotal = 0;
    for (const a of agents) {
      for (const f of FIELDS) {
        const c = a[f.key] as unknown;
        if (isCell(c)) { conf[c.conf]++; cellTotal++; }
      }
      for (const fc of Object.values(a.features ?? {})) {
        if (isCell(fc)) { conf[(fc as Cell<unknown>).conf]++; cellTotal++; }
      }
    }

    const byStatus = { active: 0, defunct: 0, unknown: 0 } as Record<string, number>;
    const byVerification = { verified: 0, community: 0, unverified: 0 } as Record<string, number>;
    for (const a of agents) {
      byStatus[a.status.v ?? "unknown"] = (byStatus[a.status.v ?? "unknown"] ?? 0) + 1;
      byVerification[a.verification] = (byVerification[a.verification] ?? 0) + 1;
    }

    const prices = dataset.shippingPrices;
    const sourced = prices.filter((p) => !p.modeled).length;

    // Canonical reconciliation, computed live.
    const slugIndex = new Set<string>();
    for (const a of agents) for (const k of [a.id, a.name, ...a.aliases]) slugIndex.add(slug(k));
    const canonSources: { identifiers: string[] }[] =
      (canonical as any).sources ?? [{ identifiers: (canonical as any).identifiers ?? [] }];
    const canonicalIds = Array.from(new Set(canonSources.flatMap((s) => s.identifiers)));
    const covered = canonicalIds.filter((c) => slugIndex.has(slug(c))).length;

    const observed = dataset.referenceQuotes.quotes.length;
    return { n, fieldCoverage, conf, cellTotal, byStatus, byVerification, observed,
      prices: prices.length, sourced, modeled: prices.length - sourced,
      lines: dataset.shippingLines.length, countries: supportedCountryCodes.length,
      sources: Object.keys(dataset.sources).length,
      canonical: { total: canonicalIds.length, covered } };
  }, []);

  const confColor: Record<Confidence, string> = {
    high: "var(--conf-high)", medium: "var(--conf-medium)", low: "var(--conf-low)", unknown: "var(--conf-unknown)",
  };

  return (
    <div style={{ maxWidth: 900 }}>
      <h1>Data coverage &amp; transparency</h1>
      <p className="muted" style={{ marginTop: -6 }}>
        A live audit of exactly what this dataset knows and doesn’t. Every gap here is an explicit
        “Unknown,” never a guess — this page exists so the honesty policy is measurable, not just claimed.
      </p>

      <div className="grid cards mt">
        <div className="card card-pad"><div className="tiny faint">AGENTS</div><div style={{ fontSize: "1.8rem", fontWeight: 700 }}>{stats.n}</div>
          <div className="tiny muted">{stats.byStatus.active} active · {stats.byStatus.defunct} defunct · {stats.byStatus.unknown} unknown</div></div>
        <div className="card card-pad"><div className="tiny faint">CANONICAL COVERAGE</div><div style={{ fontSize: "1.8rem", fontWeight: 700 }}>{stats.canonical.covered}/{stats.canonical.total}</div>
          <div className="tiny muted">of cachho/cn-links ground-truth list; roster is a superset</div></div>
        <div className="card card-pad"><div className="tiny faint">LIVE SHIPPING QUOTES</div><div style={{ fontSize: "1.8rem", fontWeight: 700, color: "var(--good)" }}>{stats.observed.toLocaleString()}</div>
          <div className="tiny muted">real totals captured from agents’ own calculators · {stats.countries} countries · 100% sourced, none modeled</div></div>
        <div className="card card-pad"><div className="tiny faint">CITATIONS</div><div style={{ fontSize: "1.8rem", fontWeight: 700 }}>{stats.sources}</div>
          <div className="tiny muted">distinct sources referenced by data cells</div></div>
      </div>

      <div className="card card-pad mt2">
        <h3>Per-field coverage (share of agents with known data)</h3>
        {stats.fieldCoverage.map((f) => (
          <BarRow key={f.label} label={f.label} value={f.pct * 100} />
        ))}
        <p className="tiny faint mt">Low bars are honest gaps, not errors — they mark where verified data is still missing and where importers should focus next.</p>
      </div>

      <div className="card card-pad mt2">
        <h3>Confidence distribution</h3>
        <p className="tiny faint" style={{ marginTop: -4 }}>Across {stats.cellTotal} tracked data cells (scalar fields + feature flags).</p>
        <div className="row" style={{ height: 26, borderRadius: 6, overflow: "hidden", border: "1px solid var(--border)" }}>
          {(["high", "medium", "low", "unknown"] as Confidence[]).map((c) =>
            stats.conf[c] > 0 ? (
              <div key={c} title={`${c}: ${stats.conf[c]}`} style={{ width: `${(stats.conf[c] / stats.cellTotal) * 100}%`, background: confColor[c] }} />
            ) : null
          )}
        </div>
        <div className="row wrap mt" style={{ gap: 14 }}>
          {(["high", "medium", "low", "unknown"] as Confidence[]).map((c) => (
            <span key={c} className="row small" style={{ gap: 6 }}>
              <span style={{ width: 12, height: 12, borderRadius: 3, background: confColor[c] }} />
              {c}: <strong>{stats.conf[c]}</strong> ({pct(stats.conf[c] / stats.cellTotal)})
            </span>
          ))}
        </div>
        <p className="tiny faint mt">
          Most values are medium/low confidence by design: this is a community-sourced market with no
          official data feeds. The point is that each value’s confidence is <em>declared</em> and
          traceable — see any value’s sources on its <Link to="/">agent page</Link>.
        </p>
      </div>

      <p className="tiny faint mt2">Snapshot {dataset.meta.generatedAt}. Reproduce with <code>npm run validate &amp;&amp; npm run reconcile</code>.</p>
    </div>
  );
}
